/**
 * Future Target Screen (Step 5)
 * Target units, price_future, cogs_future, cac_future, churn_future. Optional constraint_type, constraint_severity.
 */
import React, { useState, useEffect } from 'react';
import { Form, InputNumber, Select, Card, Row, Col, Button, Typography, Table } from 'antd';
import {
  getProductDomainSession,
  getProductDomainPhaseData,
  upsertProductDomainPhaseData,
  getProductDomainFinancialSummary,
} from '../../services/productDomainSessionApi';

const { Text } = Typography;

interface FutureTargetScreenProps {
  sessionId: string;
  onNext: () => void;
}

export const FutureTargetScreen: React.FC<FutureTargetScreenProps> = ({
  sessionId,
  onNext,
}) => {
  const [session, setSession] = useState<{ product_type?: string | null } | null>(null);
  const [financial, setFinancial] = useState<{ current_value_ratio: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const isRecurring = session?.product_type === 'recurring';

  useEffect(() => {
    const load = async () => {
      const [s, phaseData, finRes] = await Promise.all([
        getProductDomainSession(sessionId),
        getProductDomainPhaseData(sessionId),
        getProductDomainFinancialSummary(sessionId),
      ]);
      setSession(s);
      if (finRes?.financial_summary) {
        setFinancial({ current_value_ratio: finRes.financial_summary.current_value_ratio });
      }
      const future = phaseData?.phase_data?.future_target;
      if (future) {
        const byQ: Record<string, number | string> = {};
        for (const item of future) {
          if (item.value_number != null) byQ[item.question_id] = item.value_number;
          if (item.value_text) byQ[item.question_id] = item.value_text;
        }
        form.setFieldsValue({
          target_units: byQ.target_units,
          price_future: byQ.price_future,
          cogs_future: byQ.cogs_future,
          cac_future: byQ.cac_future,
          churn_rate_future: byQ.churn_rate_future,
          transition_duration_periods: byQ.transition_duration_periods,
        });
      }
      const growth = phaseData?.phase_data?.growth_constraint;
      if (growth) {
        for (const item of growth) {
          if (item.question_id === 'constraint_type') form.setFieldValue('constraint_type', item.value_text);
          if (item.question_id === 'constraint_severity') form.setFieldValue('constraint_severity', item.value_text);
        }
      }
      setLoading(false);
    };
    load();
  }, [sessionId, form]);

  const values = Form.useWatch([], form) || {};
  const targetUnits = values.target_units ?? 0;
  const priceFuture = values.price_future ?? 0;
  const cogsFuture = values.cogs_future ?? 0;
  const cacFuture = values.cac_future ?? 0;
  const revenueDelta = targetUnits * priceFuture;
  const expenseDelta = targetUnits * (cogsFuture + cacFuture);
  const targetVR = expenseDelta > 0 ? revenueDelta / expenseDelta : 0;
  const currentVR = financial?.current_value_ratio ?? 0;
  const vrDelta = targetVR - currentVR;

  const handleSaveAndNext = async () => {
    const v = await form.validateFields();
    const items = [
      { question_id: 'target_units', value_type: 'number' as const, value_number: v.target_units },
      { question_id: 'price_future', value_type: 'number' as const, value_number: v.price_future },
      { question_id: 'cogs_future', value_type: 'number' as const, value_number: v.cogs_future },
      { question_id: 'cac_future', value_type: 'number' as const, value_number: v.cac_future },
      { question_id: 'transition_duration_periods', value_type: 'number' as const, value_number: v.transition_duration_periods },
    ];
    if (isRecurring && v.churn_rate_future != null) {
      items.push({ question_id: 'churn_rate_future', value_type: 'number' as const, value_number: v.churn_rate_future });
    }
    setSaving(true);
    await Promise.all([
      upsertProductDomainPhaseData(sessionId, 'future_target', items),
      v.constraint_type != null && v.constraint_severity != null
        ? upsertProductDomainPhaseData(sessionId, 'growth_constraint', [
            { question_id: 'constraint_type', value_type: 'select' as const, value_text: v.constraint_type },
            { question_id: 'constraint_severity', value_type: 'select' as const, value_text: v.constraint_severity },
          ])
        : Promise.resolve(null),
    ]);
    setSaving(false);
    onNext();
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <Row gutter={24}>
        <Col span={14}>
          <Card title="Future Target">
            <Form form={form} layout="vertical">
              <Form.Item name="target_units" label="Target Units" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="price_future" label="Price (future)" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="cogs_future" label="COGS (future)" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="cac_future" label="CAC (future)" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              {isRecurring && (
                <Form.Item name="churn_rate_future" label="Churn (future)">
                  <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                </Form.Item>
              )}
              <Form.Item name="transition_duration_periods" label="Transition duration (periods)" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="constraint_type" label="Constraint type">
                <Select
                  allowClear
                  options={[
                    { value: 'none', label: 'None' },
                    { value: 'demand', label: 'Demand' },
                    { value: 'supply', label: 'Supply' },
                    { value: 'delivery', label: 'Delivery' },
                    { value: 'marketfit', label: 'MarketFit' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="constraint_severity" label="Constraint severity">
                <Select
                  allowClear
                  options={[
                    { value: 'none', label: 'None' },
                    { value: 'moderate', label: 'Moderate' },
                    { value: 'severe', label: 'Severe' },
                  ]}
                />
              </Form.Item>
            </Form>
          </Card>
          <Button type="primary" onClick={handleSaveAndNext} loading={saving} style={{ marginTop: 16 }}>
            Save & Next
          </Button>
        </Col>
        <Col span={10}>
          <Card title="Comparison">
            <Table
              dataSource={[
                { key: '1', metric: 'Revenue delta', current: '—', baseline: '—', target: revenueDelta.toLocaleString() },
                { key: '2', metric: 'Expense delta', current: '—', baseline: '—', target: expenseDelta.toLocaleString() },
                { key: '3', metric: 'VR delta', current: currentVR.toFixed(2), baseline: '—', target: `${targetVR.toFixed(2)} (${vrDelta >= 0 ? '+' : ''}${vrDelta.toFixed(2)})` },
              ]}
              columns={[
                { title: 'Metric', dataIndex: 'metric', key: 'metric', width: 120 },
                { title: 'Current', dataIndex: 'current', key: 'current' },
                { title: 'Baseline', dataIndex: 'baseline', key: 'baseline' },
                { title: 'Target', dataIndex: 'target', key: 'target' },
              ]}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};
